<template>
  <BaseModal
    :is-open="isOpen"
    :title="title"
    max-width="480px"
    @close="handleCancel"
  >
    <div class="confirm-action-container">
      <!-- Warning Icon -->
      <div class="warning-icon" :class="variant">
        <span class="material-symbols-outlined">{{ iconName }}</span>
      </div>

      <!-- Main Message -->
      <p class="main-message">{{ message }}</p>

      <!-- Additional Details -->
      <div v-if="details" class="details-section">
        <p class="details-text">{{ details }}</p>
      </div>

      <!-- Action Buttons -->
      <div class="confirmation-actions">
        <button @click="handleCancel" class="btn-cancel">
          <span class="material-symbols-outlined">close</span>
          <span>{{ cancelText }}</span>
        </button>
        <button
          @click="handleConfirm"
          class="btn-confirm"
          :class="variant"
        >
          <span class="material-symbols-outlined">{{ confirmIcon }}</span>
          <span>{{ confirmText }}</span>
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import BaseModal from './BaseModal.vue'

/**
 * ConfirmActionModal Component
 *
 * Generic confirmation modal for dangerous or irreversible actions.
 * Supports different variants (warning, danger, info) and customizable text.
 *
 * @example
 * <ConfirmActionModal
 *   :is-open="showConfirm"
 *   title="End Round"
 *   message="Are you sure you want to end the current round?"
 *   variant="warning"
 *   confirm-text="End Round"
 *   @confirm="handleConfirmEndRound"
 *   @cancel="showConfirm = false"
 * />
 */

interface Props {
  isOpen: boolean
  title: string
  message: string
  details?: string
  variant?: 'warning' | 'danger' | 'info'
  confirmText?: string
  cancelText?: string
  confirmIcon?: string
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  variant: 'warning',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  confirmIcon: 'check_circle',
  details: '',
})

const emit = defineEmits<{
  confirm: []
  cancel: []
  close: []
}>()

/**
 * Icon based on variant
 */
const iconName = computed(() => {
  switch (props.variant) {
    case 'danger':
      return 'warning'
    case 'info':
      return 'info'
    default:
      return 'help'
  }
})

import { computed } from 'vue'

/**
 * Handle confirm
 */
function handleConfirm(): void {
  emit('confirm')
}

/**
 * Handle cancel
 */
function handleCancel(): void {
  emit('cancel')
  emit('close')
}
</script>

<style scoped>
.confirm-action-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Warning Icon */
.warning-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse-icon 2s ease-in-out infinite;
}

.warning-icon.warning {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(245, 158, 11, 0.2));
}

.warning-icon.warning .material-symbols-outlined {
  color: #fbbf24;
}

.warning-icon.danger {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2));
}

.warning-icon.danger .material-symbols-outlined {
  color: #ef4444;
}

.warning-icon.info {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 99, 235, 0.2));
}

.warning-icon.info .material-symbols-outlined {
  color: #3b82f6;
}

.warning-icon .material-symbols-outlined {
  font-size: 3rem;
}

@keyframes pulse-icon {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.4);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(251, 191, 36, 0);
  }
}

.warning-icon.danger {
  animation-name: pulse-icon-danger;
}

@keyframes pulse-icon-danger {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(239, 68, 68, 0);
  }
}

/* Main Message */
.main-message {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  line-height: 1.6;
  margin: 0;
  max-width: 400px;
}

/* Details Section */
.details-section {
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1rem;
}

.details-text {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  line-height: 1.5;
  margin: 0;
}

/* Action Buttons */
.confirmation-actions {
  display: flex;
  gap: 1rem;
  width: 100%;
  margin-top: 0.5rem;
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
}

.btn-cancel {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.btn-confirm {
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.btn-confirm.warning {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #1a1a1a;
}

.btn-confirm.warning:hover {
  background: linear-gradient(135deg, #fcd34d 0%, #fbbf24 100%);
  box-shadow: 0 6px 16px rgba(251, 191, 36, 0.4);
  transform: translateY(-2px);
}

.btn-confirm.danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.btn-confirm.danger:hover {
  background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.4);
  transform: translateY(-2px);
}

.btn-confirm.info {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.btn-confirm.info:hover {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
  transform: translateY(-2px);
}

.btn-confirm:active {
  transform: translateY(0);
}

.btn-cancel .material-symbols-outlined,
.btn-confirm .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Responsive */
@media (max-width: 640px) {
  .warning-icon {
    width: 64px;
    height: 64px;
  }

  .warning-icon .material-symbols-outlined {
    font-size: 2.5rem;
  }

  .main-message {
    font-size: 0.9375rem;
  }

  .confirmation-actions {
    flex-direction: column;
  }
}
</style>
